import type { MoveRecord } from '@chessbut/engine';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { type VariantUi, pieceImage } from '../variants/ui.ts';

interface MoveListProps {
  history: readonly MoveRecord[];
  ui: VariantUi;
  /** Ply being viewed (number of moves played), or null for the live position. */
  viewPly: number | null;
  onSelect(ply: number | null): void;
}

/** Consecutive plies by the same side form one turn (variants where a side moves twice). */
function turns(history: readonly MoveRecord[], hidden?: (rec: MoveRecord) => boolean): { color: 0 | 1; plies: number[] }[] {
  const out: { color: 0 | 1; plies: number[] }[] = [];
  history.forEach((rec, ply) => {
    if (hidden?.(rec)) return;
    const last = out[out.length - 1];
    if (last && last.color === rec.color) last.plies.push(ply);
    else out.push({ color: rec.color, plies: [ply] });
  });
  return out;
}

export function MoveList({ history, ui, viewPly, onSelect }: MoveListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (viewPly === null) listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [history.length, viewPly]);

  const ts = turns(history, ui.hideInMoveList);
  const rows: { white?: number[]; black?: number[] }[] = [];
  for (const t of ts) {
    const row = rows[rows.length - 1];
    if (t.color === 0 || !row || row.black) rows.push(t.color === 0 ? { white: t.plies } : { black: t.plies });
    else row.black = t.plies;
  }

  const cell = (plies: number[] | undefined, key: string) => {
    if (!plies) return <span key={key} />;
    return (
      <span key={key} className="move-cell">
        {plies.map((ply) => {
          const rec = history[ply];
          const current = viewPly === null ? ply === history.length - 1 : ply === viewPly - 1;
          const badge = ui.moveBadge?.(rec);
          return (
            <button key={ply} className={`move ${current ? 'current' : ''}`} onClick={() => onSelect(ply + 1 === history.length ? null : ply + 1)}>
              {ui.san?.(rec) ?? rec.san}
              {badge && (
                <span className="move-badge" title={badge.title}>
                  <img src={badge.icon} alt="" />
                  {badge.victim ? (
                    <>
                      ×<img src={pieceImage(badge.victim)} alt="" />
                    </>
                  ) : null}
                </span>
              )}
            </button>
          );
        })}
      </span>
    );
  };

  const last = history.length;
  const at = viewPly ?? last;
  return (
    <div className="move-list-wrap">
      <div className="move-list" ref={listRef}>
        {rows.length ? (
          <div className="move-row move-head" aria-hidden>
            <span />
            <span>White</span>
            <span>Black</span>
          </div>
        ) : null}
        {rows.length ? (
          rows.map((row, i) => (
            <div className="move-row" key={i}>
              <span className="move-no">{i + 1}.</span>
              {cell(row.white, 'w')}
              {cell(row.black, 'b')}
            </div>
          ))
        ) : (
          <p className="muted small">No moves yet. White starts.</p>
        )}
      </div>
      <div className="history-nav">
        <button aria-label="First move" disabled={at === 0} onClick={() => onSelect(0)}>
          <ChevronsLeft className="icon" aria-hidden />
        </button>
        <button aria-label="Previous move" disabled={at === 0} onClick={() => onSelect(at - 1)}>
          <ChevronLeft className="icon" aria-hidden />
        </button>
        <button aria-label="Next move" disabled={at >= last} onClick={() => onSelect(at + 1 >= last ? null : at + 1)}>
          <ChevronRight className="icon" aria-hidden />
        </button>
        <button aria-label="Latest move" disabled={at >= last} onClick={() => onSelect(null)}>
          <ChevronsRight className="icon" aria-hidden />
        </button>
      </div>
    </div>
  );
}
